import { useState } from "react";
import { useCreateBed } from "../../hooks/useGreenhouseBeds";
import type { GreenhouseBed } from "../../services/greenhouseBedsApi";
import { useConfirm, withConfirmSuccess } from "@/shared/contexts/confirm-context";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import { Plus } from "lucide-react";

interface CreateGreenhouseBedDialogProps {
  bayId: string;
  baySize: number;
  existingBeds: GreenhouseBed[];
}

const initialFormData = {
  bedNumber: "",
  description: "",
  sizeSquareMeters: "",
};

export const CreateGreenhouseBedDialog = ({
  bayId,
  baySize,
  existingBeds,
}: CreateGreenhouseBedDialogProps) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const createMutation = useCreateBed();
  const confirm = useConfirm();

  const handleChange = (
    field: keyof typeof initialFormData,
    value: string
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    const bedSize = parseFloat(formData.sizeSquareMeters);
    
    // Validation: Bed size cannot exceed bay size
    if (bedSize > baySize) {
      setFieldErrors({
        sizeSquareMeters: [`Bed size cannot exceed bay size of ${baySize}m²`],
      });
      return;
    }

    // Validation: Total bed sizes cannot exceed bay size
    const totalExistingSize = existingBeds.reduce((sum, bed) => sum + bed.sizeSquareMeters, 0);
    if (totalExistingSize + bedSize > baySize) {
      const availableSpace = baySize - totalExistingSize;
      setFieldErrors({
        sizeSquareMeters: [`Available space in bay is ${availableSpace.toFixed(2)}m². Current beds use ${totalExistingSize.toFixed(2)}m².`],
      });
      return;
    }

    const payload = {
      bayId,
      bedNumber: formData.bedNumber,
      description: formData.description,
      sizeSquareMeters: bedSize,
    };

    confirm({
      title: "Create bed?",
      description: `Bed “${formData.bedNumber}” will be added to this bay.`,
      confirmLabel: "Create bed",
      onConfirm: () =>
        withConfirmSuccess(createMutation.mutateAsync(payload), "Bed created")
          .then(() => {
            setFormData(initialFormData);
            setOpen(false);
          })
          .catch((error: any) => {
            if (error.response?.data?.errors) {
              setFieldErrors(error.response.data.errors);
            }
            throw error;
          }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2" size="sm">
          <Plus className="w-4 h-4" />
          Create Bed
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create New Bed</DialogTitle>
          <DialogDescription>Add a new bed to this bay</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {createMutation.error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
              {createMutation.error.message}
            </div>
          )}

          <Field>
            <FieldLabel htmlFor="bedNumber">
              Bed Number
            </FieldLabel>
            <Input
              id="bedNumber"
              placeholder="e.g., Bed 1"
              value={formData.bedNumber}
              onChange={(e) => handleChange("bedNumber", e.target.value)}
              disabled={createMutation.isPending}
            />
            {fieldErrors.bedNumber && (
              <div className="text-red-500 text-xs mt-1">
                {fieldErrors.bedNumber.join(", ")}
              </div>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="description">Description</FieldLabel>
            <Textarea
              id="description"
              placeholder="Bed details..."
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              disabled={createMutation.isPending}
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="sizeSquareMeters">
              Size (m²) <span className="text-red-500">*</span>
            </FieldLabel>
            <Input
              id="sizeSquareMeters"
              type="number"
              step="0.01"
              placeholder="0.00"
              value={formData.sizeSquareMeters}
              onChange={(e) => handleChange("sizeSquareMeters", e.target.value)}
              required
              disabled={createMutation.isPending}
            />
            {fieldErrors.sizeSquareMeters && (
              <div className="text-red-500 text-xs mt-1">
                {fieldErrors.sizeSquareMeters.join(", ")}
              </div>
            )}
          </Field>

          <Button
            type="submit"
            className="w-full"
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? "Creating..." : "Create Bed"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};